import { useEffect, useMemo, useState, type Dispatch } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"

import { Checkbox } from "@/components/ui/checkbox"
import {
  useActionCategories,
  useInterestCategories,
  usePurchaseIntentionSearch,
} from "@/api/hooks/campaignForm"

import { IntuitiveMultiSelect } from "./IntuitiveMultiSelect"
import { TargetingTreeSelect } from "./TargetingTreeSelect"
import type { FormAction, FormState, InterestBehaviorTab } from "../state/formReducer"

const TABS: { id: InterestBehaviorTab; label: string }[] = [
  { id: "interests", label: "Interests" },
  { id: "purchase_intention", label: "Purchase intention" },
  { id: "video", label: "Video interactions" },
  { id: "creator", label: "Creator interactions" },
]

type TargetingBucket =
  | "interestCategoryIds"
  | "purchaseIntentionKeywordIds"
  | "videoActionCategoryIds"
  | "creatorActionCategoryIds"

const SELECTION_GROUPS: {
  tab: InterestBehaviorTab
  label: string
  bucket: TargetingBucket
  ids: (state: FormState) => string[]
}[] = [
  {
    tab: "interests",
    label: "Interests",
    bucket: "interestCategoryIds",
    ids: (s) => s.interestCategoryIds,
  },
  {
    tab: "purchase_intention",
    label: "Purchase intention",
    bucket: "purchaseIntentionKeywordIds",
    ids: (s) => s.purchaseIntentionKeywordIds,
  },
  {
    tab: "video",
    label: "Video interactions",
    bucket: "videoActionCategoryIds",
    ids: (s) => s.videoActionCategoryIds,
  },
  {
    tab: "creator",
    label: "Creator interactions",
    bucket: "creatorActionCategoryIds",
    ids: (s) => s.creatorActionCategoryIds,
  },
]

interface Props {
  state: FormState
  dispatch: Dispatch<FormAction>
}

function selectedCount(state: FormState): number {
  return (
    state.interestCategoryIds.length +
    state.purchaseIntentionKeywordIds.length +
    state.videoActionCategoryIds.length +
    state.creatorActionCategoryIds.length
  )
}

function SelectedAcrossGroups({
  state,
  dispatch,
}: {
  state: FormState
  dispatch: Dispatch<FormAction>
}) {
  const groups = SELECTION_GROUPS.map((g) => ({
    ...g,
    chips: g.ids(state).map((id) => ({
      id,
      name: state.targetingLabels[id] ?? id,
    })),
  })).filter((g) => g.chips.length > 0)

  if (groups.length === 0) {
    return (
      <p className="mb-3 text-sm text-white/70">No interests or behaviors selected</p>
    )
  }

  return (
    <div className="mb-3 rounded-md border border-[#5a5a5a] bg-[#1f1f1f] p-2.5">
      <div className="space-y-2.5">
        {groups.map((group) => (
          <div key={group.bucket}>
            <button
              type="button"
              className="mb-1.5 text-left text-xs font-medium text-white/80 hover:text-white"
              onClick={() =>
                dispatch({ type: "SET_INTEREST_BEHAVIOR_TAB", tab: group.tab })
              }
            >
              {group.chips.length} {group.label}
            </button>
            <div className="flex flex-wrap gap-2">
              {group.chips.map((chip) => (
                <button
                  key={`${group.bucket}:${chip.id}`}
                  type="button"
                  onClick={() =>
                    dispatch({
                      type: "TOGGLE_TARGETING_OPTION",
                      bucket: group.bucket,
                      id: chip.id,
                      name: chip.name,
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-[#8fa0ef]/45 bg-[#3a4060] px-2 py-0.5 text-xs text-white hover:bg-[#4a5380]"
                  aria-label={`Remove ${chip.name}`}
                >
                  {chip.name}
                  <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function InterestsBehaviorsPicker({ state, dispatch }: Props) {
  const [open, setOpen] = useState(false)
  const total = selectedCount(state)
  const smartEnabled = total > 0

  // Only fetch catalogs while expanded (or if selections already exist).
  const advertiserId =
    open || total > 0 ? state.advertiserId : null

  const [debouncedPurchaseSearch, setDebouncedPurchaseSearch] = useState(
    state.purchaseIntentionSearch,
  )
  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedPurchaseSearch(state.purchaseIntentionSearch),
      300,
    )
    return () => window.clearTimeout(t)
  }, [state.purchaseIntentionSearch])

  const interestsQuery = useInterestCategories(advertiserId)
  const videoQuery = useActionCategories(advertiserId, "VIDEO_RELATED")
  const creatorQuery = useActionCategories(advertiserId, "CREATOR_RELATED")
  const purchaseQuery = usePurchaseIntentionSearch(
    advertiserId,
    debouncedPurchaseSearch,
    open &&
      (state.interestBehaviorTab === "purchase_intention" ||
        state.purchaseIntentionKeywordIds.length > 0),
  )

  const interestTree = interestsQuery.data?.tree ?? []
  const videoTree = videoQuery.data?.tree ?? []
  const creatorTree = creatorQuery.data?.tree ?? []

  const purchaseOptions = useMemo(() => {
    const fromApi = purchaseQuery.data ?? []
    const selectedMissing = state.purchaseIntentionKeywordIds
      .filter((id) => !fromApi.some((o) => o.id === id))
      .map((id) => ({
        id,
        name: state.targetingLabels[id] ?? id,
      }))
    return [...fromApi, ...selectedMissing]
  }, [
    purchaseQuery.data,
    state.purchaseIntentionKeywordIds,
    state.targetingLabels,
  ])

  const summary =
    total === 0
      ? "All (broad targeting)"
      : `${total} selected${
          state.smartInterestBehaviorEnabled ? " · Smart on" : ""
        }`

  return (
    <div className="mt-4 rounded-md border border-[#5a5a5a] bg-[#262626]">
      <div className="flex items-start gap-1 px-2 py-1.5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 rounded px-1 py-1 text-left hover:bg-[#2d2d2d]"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-white/60" />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-white/60" />
          )}
          <div className="min-w-0 flex-1">
            <span className="text-sm font-semibold text-white">
              Interests &amp; behaviors
            </span>
            {!open ? (
              <p className="mt-0.5 text-xs text-white/60">{summary}</p>
            ) : null}
          </div>
        </button>
        {total > 0 && open ? (
          <button
            type="button"
            className="shrink-0 px-2 py-1 text-xs text-[#aab6ff] hover:underline"
            onClick={() => dispatch({ type: "CLEAR_INTEREST_BEHAVIOR" })}
          >
            Clear all ({total})
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="border-t border-[#4a4a4a] px-3 pb-3 pt-3">
          <SelectedAcrossGroups state={state} dispatch={dispatch} />

          <div className="mb-3 flex flex-wrap gap-1">
            {TABS.map((tab) => {
              const active = state.interestBehaviorTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() =>
                    dispatch({ type: "SET_INTEREST_BEHAVIOR_TAB", tab: tab.id })
                  }
                  className={
                    active
                      ? "rounded-md bg-[#4a5380] px-2.5 py-1 text-xs text-white"
                      : "rounded-md border border-[#5a5a5a] bg-[#2d2d2d] px-2.5 py-1 text-xs text-white/80 hover:bg-[#3a3a3a]"
                  }
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {state.interestBehaviorTab === "interests" ? (
            <TargetingTreeSelect
              tree={interestTree}
              selected={state.interestCategoryIds}
              labels={state.targetingLabels}
              onSelectionChange={(items) =>
                dispatch({
                  type: "SET_TARGETING_OPTIONS",
                  bucket: "interestCategoryIds",
                  items,
                })
              }
              search={state.interestsSearch}
              onSearchChange={(value) =>
                dispatch({ type: "SET_INTERESTS_SEARCH", value })
              }
              searchPlaceholder="Search interests..."
              showSelectionSummary={false}
            />
          ) : null}

          {state.interestBehaviorTab === "purchase_intention" ? (
            <>
              <IntuitiveMultiSelect
                options={purchaseOptions}
                selected={state.purchaseIntentionKeywordIds}
                onToggle={(id) => {
                  const opt = purchaseOptions.find((o) => o.id === id)
                  dispatch({
                    type: "TOGGLE_TARGETING_OPTION",
                    bucket: "purchaseIntentionKeywordIds",
                    id,
                    name: opt?.name ?? state.targetingLabels[id] ?? id,
                  })
                }}
                search={state.purchaseIntentionSearch}
                onSearchChange={(value) =>
                  dispatch({ type: "SET_PURCHASE_INTENTION_SEARCH", value })
                }
                searchPlaceholder="Search or browse suggestions..."
                showSelectionSummary={false}
                filterOptionsBySearch={false}
              />
              {purchaseQuery.isError ? (
                <p className="mt-1 text-xs text-red-300">
                  Could not load purchase intention keywords. Try again.
                </p>
              ) : null}
              {purchaseQuery.isFetching ? (
                <p className="mt-1 text-xs text-white/60">
                  {state.purchaseIntentionSearch.trim()
                    ? "Searching…"
                    : "Loading suggestions…"}
                </p>
              ) : null}
            </>
          ) : null}

          {state.interestBehaviorTab === "video" ? (
            <TargetingTreeSelect
              tree={videoTree}
              selected={state.videoActionCategoryIds}
              labels={state.targetingLabels}
              onSelectionChange={(items) =>
                dispatch({
                  type: "SET_TARGETING_OPTIONS",
                  bucket: "videoActionCategoryIds",
                  items,
                })
              }
              search={state.videoActionsSearch}
              onSearchChange={(value) =>
                dispatch({ type: "SET_VIDEO_ACTIONS_SEARCH", value })
              }
              searchPlaceholder="Search video interaction categories..."
              showSelectionSummary={false}
            />
          ) : null}

          {state.interestBehaviorTab === "creator" ? (
            <TargetingTreeSelect
              tree={creatorTree}
              selected={state.creatorActionCategoryIds}
              labels={state.targetingLabels}
              onSelectionChange={(items) =>
                dispatch({
                  type: "SET_TARGETING_OPTIONS",
                  bucket: "creatorActionCategoryIds",
                  items,
                })
              }
              search={state.creatorActionsSearch}
              onSearchChange={(value) =>
                dispatch({ type: "SET_CREATOR_ACTIONS_SEARCH", value })
              }
              searchPlaceholder="Search creator interaction categories..."
              showSelectionSummary={false}
            />
          ) : null}

          <label
            htmlFor="smart-interest-behavior"
            className={
              smartEnabled
                ? "mt-3 flex cursor-pointer items-center gap-2 rounded-md border border-[#5a5a5a] bg-[#2d2d2d] px-3 py-2"
                : "mt-3 flex cursor-not-allowed items-center gap-2 rounded-md border border-[#5a5a5a] bg-[#2d2d2d] px-3 py-2 opacity-50"
            }
          >
            <Checkbox
              id="smart-interest-behavior"
              checked={smartEnabled && state.smartInterestBehaviorEnabled}
              disabled={!smartEnabled}
              onCheckedChange={(checked) => {
                if (!smartEnabled) return
                dispatch({
                  type: "SET_SMART_INTEREST_BEHAVIOR",
                  value: checked === true,
                })
              }}
            />
            <span className="text-sm text-white">
              Smart interests &amp; behaviors
            </span>
          </label>
          {!smartEnabled ? (
            <p className="mt-1 text-xs text-brand-accent">
              ℹ️ Select at least one interest or behavior to enable Smart
              interests &amp; behaviors
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
